// src/guards/RequireProAccess.tsx
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { hasProClaim } from '../lib/auth/claimsUtils';

interface RequireProAccessProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * RequireProAccess Guard
 * 
 * Protects Pro-only routes from SRL access.
 * 
 * Access Rules:
 * - Requires Pro claim (checked via hasProClaim — accepts isPro, hasPro, or pro)
 * - OR admin/super role (for support/debugging)
 * 
 * Redirects:
 * - Not authenticated → /pro/login
 * - Authenticated but not Pro → /dashboard (SRL dashboard)
 */
export default function RequireProAccess({ children, redirectTo = '/dashboard' }: RequireProAccessProps) {
  const { user, claims, loading } = useAuth();

  // Loading guard - show loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-purple-600 border-t-transparent"></div>
          <p className="text-slate-400">Verifying professional access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to pro login
  if (!user) {
    if (import.meta.env.DEV) {
      console.info('[AuthDebug] RequireProAccess: No user, redirecting to /pro/login');
    }
    return <Navigate to="/pro/login" replace />;
  }

  // CRITICAL: Claims are fetched async AFTER status becomes "signedIn".
  // If claims is undefined the token fetch hasn't completed yet — wait rather
  // than redirect, otherwise pro users are kicked to /dashboard on every load.
  if (claims === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-purple-600 border-t-transparent"></div>
          <p className="text-slate-400">Verifying professional access...</p>
        </div>
      </div>
    );
  }

  // Check Pro access: accept all three claim variants set by onLoginSetClaims
  // (isPro is canonical; hasPro and pro are legacy aliases also written by the function)
  // smartRouting.ts checks all three variants — this guard must match.
  const hasPro = hasProClaim(claims);
  const isAdmin = claims?.admin === true || claims?.super === true;
  const isSuper = claims?.role === 'super';

  // Log admin bypass for audit purposes
  if (!hasPro && (isAdmin || isSuper)) {
    console.warn('[ProAccess] Admin/super bypassing Pro requirement', {
      uid: user?.uid,
      email: user?.email,
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
    });
  }

  if (import.meta.env.DEV) {
    console.info('[AuthDebug] RequireProAccess', {
      hasPro,
      isAdmin,
      isSuper,
      allowAccess: hasPro || isAdmin || isSuper,
      claims: {
        'claims.admin': claims?.admin,
        'claims.super': claims?.super,
        'claims.role': claims?.role,
      }
    });
  }

  // Authenticated but not pro - redirect to SRL dashboard
  if (!hasPro && !isAdmin && !isSuper) {
    if (import.meta.env.DEV) {
      console.warn('[AuthDebug] RequireProAccess: SRL attempting Pro access, redirecting to', redirectTo);
    }
    return <Navigate to={redirectTo} replace />;
  }

  // User has Pro access - render children
  return <>{children}</>;
}

